import {
  PIPE_MATERIAL_NAMES,
  STOCK_SETTING_BASIS,
  pipeMaterialOf,
  type PipeDiameterTotal,
  type PlanPipeMaterial,
  type StockLine,
  type StockReport,
} from "@sts/rules-data";
import { esc, isoDateToLong, num } from "./render.js";
import type { DocumentHeader, DocumentTemplate, RenderedDocument } from "./types.js";

/**
 * The installer pack — the job pack handed to the installation team.
 *
 * Asked for on 2026-09-10: "there should be one installer pack … which we
 * gonna hand over to the installer, then he knows exactly how much stock he
 * needs to take, how much pipeline is going to be there, how many connectors …
 * and we should be able to download the PDF file."
 *
 * SECTION ORDER is the order the pack is used on site, from the research note
 * `docs/research/installer-pack-2026-09-10.md` §1: job card, pre-start, the
 * design brief, the property, the plans, the outdoor unit, the radiator and
 * pipe schedules, the pick list, water treatment and commissioning, as-built
 * deviations and sign-off, and what comes back for the customer's handover
 * pack.
 *
 * THE ONE MCS "SHALL". MIS 3005-D:2025 cl. 3.8.1: "Prior to the installation
 * commencing, the installer shall be provided in writing with" items (a)–(h)
 * (research, key finding 1). Section 3 prints each from the stored design; an
 * item the design has not produced prints "Not yet designed — do not start".
 * No number is ever invented to fill a gap.
 *
 * A PACK IS WRITTEN ON. Everything Scannix does not hold — install dates, the
 * team, the stopcock, clearances, measured commissioning values, serial
 * numbers — is a ruled blank, not an omission. Unlike the design pack, this
 * renders while the design is incomplete: the gaps are printed as gaps.
 *
 * DRAFT until issued. An unissued pack carries a DRAFT watermark on every page
 * and in its title; issuing stores that exact HTML as a hashed, immutable
 * document (see `apps/api/src/http/installer-pack-routes.ts`).
 *
 * DETERMINISTIC like every template: no clock, no locale, no randomness.
 */

export const NOT_DESIGNED = "Not yet designed — do not start";

export interface InstallerPackRoomLoss {
  readonly name: string;
  readonly designTempC: number;
  readonly totalW: number;
  readonly wattsPerM2: number;
}

export interface InstallerPackEmitter {
  readonly roomName: string;
  readonly type: string;
  readonly dimensions: string;
  readonly outputAtDesignFlowW: number;
  readonly action: "retain" | "replace" | "new";
}

/**
 * MIS 3005-D 3.8.1 (a)–(h). Every field is null when the design has not
 * produced it; the template prints `NOT_DESIGNED` for a null.
 */
export interface InstallerPackDesignBrief {
  /** (a) */
  readonly roomLosses: readonly InstallerPackRoomLoss[] | null;
  readonly wholeDwelling: { readonly totalW: number; readonly wattsPerM2: number } | null;
  /** (b) */
  readonly dailyHotWater: { readonly litres: number; readonly basis: string } | null;
  /** (c) */
  readonly emitters: readonly InstallerPackEmitter[] | null;
  /** (c) Rooms in the heat loss with no emitter decided — printed as not designed. */
  readonly emittersMissingFor: readonly string[];
  /** (d) */
  readonly designEmitterTemp: { readonly celsius: number; readonly worstRoom: string | null } | null;
  /** (e) */
  readonly cylinder: {
    readonly manufacturer: string;
    readonly model: string;
    readonly volumeLitres: number;
    readonly recoveryMinutes: number;
    readonly coilAreaM2?: number;
  } | null;
  /** (f) */
  readonly designFlowTempC: number | null;
  /**
   * The flow − return difference the design is worked at: the designer's
   * (design decisions, migration 0035), or the design pack's 5 K when none is
   * set — `assumed` says which. Absent: printed as 5 K assumed.
   */
  readonly systemDeltaT?: { readonly kelvin: number; readonly assumed: boolean };
  /** (g) */
  readonly heatPump: {
    readonly manufacturer: string;
    readonly model: string;
    readonly scop: number;
    readonly scopSource: string;
  } | null;
  /** (h) */
  readonly metering: string | null;
}

export interface InstallerPackPlan {
  readonly storey: number;
  readonly storeyLabel: string;
  /** The inline SVG from `renderPlanSvg`, or null when there is nothing to draw. */
  readonly svg: string | null;
  readonly scale: number | null;
  readonly note: string | null;
}

export interface InstallerPackOutdoorUnit {
  readonly tag: string;
  readonly name: string;
  readonly widthMm: number;
  readonly depthMm: number;
  readonly heightMm: number;
  readonly storeyLabel: string;
  readonly fromCatalogue: boolean;
}

export interface InstallerPackRadiator {
  readonly tag: string;
  readonly storeyLabel: string;
  readonly room: string | null;
  readonly panelType: string;
  readonly typeAlias: string;
  readonly heightMm: number;
  readonly lengthMm: number;
  readonly depthMm: number;
  readonly label: string | null;
}

export interface InstallerPackPipeRun {
  readonly tag: string;
  readonly storeyLabel: string;
  readonly circuit: "flow" | "return";
  readonly diameterMm: number;
  /** Absent: copper, as every run was before materials were recorded. */
  readonly material?: PlanPipeMaterial;
  readonly lengthM: number;
  readonly bends: number;
  readonly label: string | null;
}

/**
 * One open comment pinned to the plan (migration 0043). `number` is the
 * thread's own — the same number its pin carries on the floor plans above and
 * on screen.
 */
export interface InstallerPackNote {
  readonly number: number;
  readonly storeyLabel: string;
  /** What it is pinned to, in words — "R2", "Wall 3 (Lounge)", "Kitchen" — or null on open floor. */
  readonly about: string | null;
  readonly body: string;
  readonly author: string | null;
  /** ISO timestamp. */
  readonly createdAt: string;
  readonly replies: readonly { readonly author: string | null; readonly body: string }[];
}

export interface InstallerPackNotes {
  /** Open threads, by number. Printed in full, pinned on the plans. */
  readonly open: readonly InstallerPackNote[];
  /** Resolved threads: counted, not printed. */
  readonly resolvedCount: number;
}

/** A cylinder or buffer drawn on the plan. */
export interface InstallerPackVessel {
  readonly tag: string;
  readonly kind: "cylinder" | "buffer" | "volumiser";
  readonly name: string;
  readonly storeyLabel: string;
  readonly volumeLitres: number;
  readonly diameterMm: number;
  readonly heightMm: number;
  /** A size from the generic set: its figures are nominal. */
  readonly nominal: boolean;
  readonly room: string | null;
}

/** One answered question of the site survey, in the words it was asked. */
export interface InstallerPackSiteAnswer {
  readonly label: string;
  /** Already formatted for print: "Single", "Yes", "100 A", "3 photos". */
  readonly value: string;
}

/** One section of the site survey, with only the questions that were answered. */
export interface InstallerPackSiteSection {
  readonly title: string;
  readonly answers: readonly InstallerPackSiteAnswer[];
}

export interface InstallerPackInput {
  readonly header: DocumentHeader;
  /** False prints DRAFT on every page. */
  readonly issued: boolean;
  readonly customer: { readonly name: string | null; readonly phone: string | null };
  readonly property: {
    readonly builtForm: string | null;
    readonly propertyType: string | null;
    readonly wallConstruction: string | null;
    readonly wallInsulation: string | null;
  };
  /** "Vaillant aroTHERM plus 7 kW" — from the plan, else the design; null if neither. */
  readonly heatPumpName: string | null;
  readonly design: InstallerPackDesignBrief;
  readonly plans: readonly InstallerPackPlan[];
  /** Why there is no plan at all, when `plans` is empty. */
  readonly plansNote: string | null;
  /**
   * Comments pinned to the plan. Absent (or none open and none resolved)
   * prints no notes section, so a pack without comments is unchanged.
   */
  readonly notes?: InstallerPackNotes;
  readonly outdoorUnits: readonly InstallerPackOutdoorUnit[];
  readonly radiators: readonly InstallerPackRadiator[];
  readonly pipeRuns: readonly InstallerPackPipeRun[];
  /** Cylinders and buffers drawn on the plan. Absent: none. */
  readonly plant?: readonly InstallerPackVessel[];
  /**
   * What the surveyor found on the visit (the portal's site survey).
   * Absent, or empty, prints no section: a pack for a job whose site survey
   * was never filled in is unchanged.
   */
  readonly siteSurvey?: readonly InstallerPackSiteSection[];
  readonly stock: StockReport;
}

// ------------------------------------------------------------ helpers --

const blank = (width: "s" | "m" | "l" = "m") => `<span class="blank blank--${width}"></span>`;
const tick = `<span class="tick" aria-hidden="true"></span>`;
/** One tick-box option or labelled blank that must not break across lines. */
const opt = (html: string) => `<span class="opt">${html}</span>`;
/** Millimetres as printed on a drawing: whole numbers, no thousands separator. */
const mmText = (value: number) => String(Math.round(value));
const notDesigned = `<span class="nd">${esc(NOT_DESIGNED)}</span>`;

function field(label: string, value: string): string {
  return `<tr><th scope="row">${esc(label)}</th><td>${value}</td></tr>`;
}

function fields(rows: readonly (readonly [string, string])[]): string {
  return `<table class="fields"><tbody>${rows.map(([k, v]) => field(k, v)).join("")}</tbody></table>`;
}

function valueOr(value: string | null | undefined, fallback = "Not recorded"): string {
  return value && value.trim() ? esc(value) : `<span class="muted">${esc(fallback)}</span>`;
}

function section(number: number, title: string, body: string, opts: { pageBreak?: boolean; id?: string } = {}): string {
  return `<section class="${opts.pageBreak ? "page-break" : ""}"${opts.id ? ` id="${opts.id}"` : ""}>
<h2><span class="sec-no">${number}</span> ${esc(title)}</h2>
${body}
</section>`;
}

function qtyText(value: number): string {
  return Number.isInteger(value) ? String(value) : num(value, 2);
}

const SOURCE_TEXT: Readonly<Record<string, string>> = {
  plan: "plan",
  design: "design",
  R: "R",
  V: "V",
  I: "I",
  RoT: "RoT",
  assumption: "assumed",
  catalogue: "catalogue",
  manual: "manual",
};

/** The running footer for the PDF, so the HTML and the PDF chrome agree. */
export function installerPackFooterText(header: DocumentHeader, issued: boolean): string {
  return [
    "Installer pack",
    header.jobReference,
    `${issued ? "Issued" : "Printed"} ${isoDateToLong(header.issuedOn)}`,
    issued ? null : "DRAFT",
  ]
    .filter((p): p is string => p !== null)
    .join(" · ");
}

// ------------------------------------------------------------- styles --

const STYLES = `
:root { --ink:#1a1a1a; --muted:#5a5a5a; --line:#b9bec2; --tint:#f1f4f5; --flow:#b3261e; --ret:#1d4f91; }
* { box-sizing: border-box; }
@page { size: A4; }
body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: var(--ink);
  line-height: 1.28; margin: 0; padding: 16px; font-size: 8.5pt; }
h1 { font-size: 15pt; margin: 0; }
h2 { font-size: 10.5pt; margin: 10px 0 4px; padding-bottom: 2px; border-bottom: 1.5px solid var(--ink);
  break-after: avoid; }
h3 { font-size: 9pt; margin: 7px 0 3px; break-after: avoid; }
.sec-no { display: inline-block; min-width: 1.6em; }
.doc-header { display: flex; justify-content: space-between; gap: 16px; border-bottom: 2px solid var(--ink);
  padding-bottom: 8px; }
.doc-header .meta { text-align: right; font-size: 9pt; color: var(--muted); }
.doc-header .meta b { color: var(--ink); }
.draft-banner { margin: 8px 0; padding: 6px 10px; border: 1.5px solid #a8410f; color: #7a2e08;
  background: #fbf1ec; font-weight: 600; }
.draft-mark { position: fixed; top: 42%; left: 0; right: 0; text-align: center; font-size: 110pt;
  font-weight: 800; color: rgba(168, 65, 15, 0.08); transform: rotate(-30deg); pointer-events: none;
  z-index: 0; letter-spacing: 12px; }
table { border-collapse: collapse; width: 100%; margin: 4px 0; font-size: 8pt; }
th, td { border: 1px solid var(--line); padding: 2.5px 5px; text-align: left; vertical-align: top; }
th { background: var(--tint); font-weight: 600; }
td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
td.c, th.c { text-align: center; }
tr { break-inside: avoid; }
table.fields th { width: 34%; }
.blank { display: inline-block; border-bottom: 1px solid var(--ink); height: 1.2em; vertical-align: bottom; }
.opt { white-space: nowrap; margin-right: 3mm; display: inline-block; }
.blank--s { width: 16mm; } .blank--m { width: 55mm; } .blank--l { width: 100%; }
.tick { display: inline-block; width: 3.6mm; height: 3.6mm; border: 1px solid var(--ink); vertical-align: -0.6mm;
  background: #fff; }
.write { height: 7mm; }
.lines div { border-bottom: 1px solid var(--line); height: 8mm; }
.nd { color: #8c1d18; font-weight: 700; }
.muted { color: var(--muted); }
.small, .basis { font-size: 6.8pt; color: var(--muted); }
.basis { margin-top: 2px; }
.warn { border-left: 3px solid #a8410f; background: #fbf1ec; padding: 3px 7px; margin: 4px 0; font-size: 7.8pt; }
.note { border-left: 3px solid var(--line); padding: 2px 7px; margin: 4px 0; font-size: 7.8pt; color: var(--muted); }
.plan { text-align: center; break-inside: avoid; }
.plan svg { max-width: 100%; height: auto; }
.pin-no { display: inline-block; min-width: 5mm; padding: 0.4mm 1mm; border-radius: 3mm; background: #c77700;
  color: #fff; font-weight: 700; text-align: center; }
.notes .note-body { white-space: pre-wrap; }
.notes .reply { margin-top: 2px; padding-left: 7px; border-left: 2px solid var(--line); white-space: pre-wrap; }
.page-break { break-before: page; }
.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
.flow { color: var(--flow); font-weight: 600; } .ret { color: var(--ret); font-weight: 600; }
.pick td.qty { font-size: 9.5pt; font-weight: 700; }
.pick .derived { font-size: 6.8pt; color: var(--muted); font-weight: 400; display: block; }
.src { font-size: 7pt; border: 1px solid var(--line); border-radius: 3px; padding: 0 3px; color: var(--muted);
  white-space: nowrap; }
footer { margin-top: 12px; padding-top: 6px; border-top: 1px solid var(--line); color: var(--muted); font-size: 8pt; }
@media print { body { padding: 0; } }
`;

// ----------------------------------------------------------- sections --

function coverSection(input: InstallerPackInput): string {
  const d = input.design;
  const cylinder = d.cylinder
    ? `${esc(`${d.cylinder.manufacturer} ${d.cylinder.model}`.trim())}, ${num(d.cylinder.volumeLitres, 0)} litres`
    : notDesigned;
  const property = [input.property.builtForm, input.property.propertyType].filter(Boolean).join(" ");
  return section(
    1,
    "Job card",
    `<div class="grid2">
${fields([
  ["Job reference", `<b>${esc(input.header.jobReference)}</b>`],
  ["Address", esc(input.header.propertyAddress)],
  ["Customer", valueOr(input.customer.name)],
  ["Customer phone", valueOr(input.customer.phone)],
  ["Heat pump", input.heatPumpName ? `<b>${esc(input.heatPumpName)}</b>` : notDesigned],
  ["Design flow temperature", d.designFlowTempC !== null ? `<b>${num(d.designFlowTempC, 1)} °C</b>` : notDesigned],
  ["Cylinder", cylinder],
  ["Property", valueOr(property || null)],
])}
${fields([
  ["Install start", blank()],
  ["Expected finish", blank()],
  ["Lead installer", blank()],
  ["Team", blank()],
  ["Electrician", blank()],
  ["Site contact and phone", blank()],
  ["Access and parking", blank()],
  ["Keys / alarm / pets", blank()],
])}
</div>`,
  );
}

function preStartSection(): string {
  return section(
    2,
    "Before you start",
    fields([
      ["Risk assessment completed (MIS 3005-I 3.1.5)", `${tick} ${opt(`By ${blank("s")}`)} ${opt(`Date ${blank("s")}`)}`],
      ["Asbestos", `${opt(`${tick} Survey seen`)} ${opt(`${tick} None suspected`)} ${opt(`${tick} Suspected — stop and report`)}`],
      ["Stopcock location", blank("l")],
      ["Consumer unit location", blank("l")],
      ["Electricity meter location", blank("l")],
      ["Existing system drained / isolated", `${tick} &nbsp;Notes ${blank()}`],
      ["Customer walk-through done (routes, radiator positions)", `${tick}`],
      ["Surveyor's site notes", "At the back of this pack, section 13 (when the site survey was filled in)."],
    ]),
  );
}

function designSection(input: InstallerPackInput): string {
  const d = input.design;
  const a = d.roomLosses && d.roomLosses.length > 0
    ? `<table><thead><tr><th>Room</th><th class="n">Design temp</th><th class="n">Heat loss (W)</th><th class="n">W/m²</th></tr></thead><tbody>
${d.roomLosses
  .map((r) => `<tr><td>${esc(r.name)}</td><td class="n">${num(r.designTempC, 1)} °C</td><td class="n">${num(r.totalW, 0)}</td><td class="n"><b>${num(r.wattsPerM2, 1)}</b></td></tr>`)
  .join("")}
</tbody>${
        d.wholeDwelling
          ? `<tfoot><tr><td><b>Whole dwelling</b></td><td></td><td class="n"><b>${num(d.wholeDwelling.totalW, 0)}</b></td><td class="n"><b>${num(d.wholeDwelling.wattsPerM2, 1)}</b></td></tr></tfoot>`
          : ""
      }</table>`
    : `<p>${notDesigned}</p>`;

  const c = d.emitters && d.emitters.length > 0
    ? `<table><thead><tr><th>Room</th><th>Emitter</th><th>Dimensions</th><th class="n">Output at design flow</th><th>Action</th></tr></thead><tbody>
${d.emitters
  .map(
    (e) =>
      `<tr><td>${esc(e.roomName)}</td><td>${esc(e.type)}</td><td>${esc(e.dimensions)}</td><td class="n">${num(e.outputAtDesignFlowW, 0)} W</td><td>${esc(
        e.action === "retain" ? "Retain existing" : e.action === "replace" ? "Replace" : "New",
      )}</td></tr>`,
  )
  .join("")}
</tbody></table>`
    : `<p>${notDesigned}</p>`;

  const missingEmitters =
    d.emittersMissingFor.length > 0
      ? `<p><span class="nd">No emitter decided for: ${esc(d.emittersMissingFor.join(", "))} — ${esc(NOT_DESIGNED.toLowerCase())}</span></p>`
      : "";

  const item = (letter: string, title: string, body: string) =>
    `<h3>(${letter}) ${esc(title)}</h3>${body}`;

  return section(
    3,
    "Design brief (MIS 3005-D 3.8.1)",
    `<p class="small">MIS 3005-D:2025 cl. 3.8.1: before installation starts the installer shall be given, in writing,
items (a)–(h) below. Where an item reads <span class="nd">${esc(NOT_DESIGNED)}</span>, the design has not produced it yet — get it from the designer before work begins.</p>
${item("a", "Heat loss of each room (W/m²)", a)}
${item("b", "Daily hot water demand", d.dailyHotWater ? `<p><b>${num(d.dailyHotWater.litres, 0)} litres/day</b> <span class="small">(${esc(d.dailyHotWater.basis)}; MIS 3005-D 3.5.2, 45 × N)</span></p>` : `<p>${notDesigned}</p>`)}
${item("c", "Emitter types and dimensions", c + (d.emitters && d.emitters.length > 0 ? missingEmitters : ""))}
${item(
  "d",
  "Design emitter temperature",
  d.designEmitterTemp
    ? `<p><b>${num(d.designEmitterTemp.celsius, 1)} °C</b>${d.designEmitterTemp.worstRoom ? ` — set by ${esc(d.designEmitterTemp.worstRoom)}, the worst-performing room` : ""}</p>`
    : `<p>${notDesigned}</p>`,
)}
${item(
  "e",
  "Cylinder specification and recovery",
  d.cylinder
    ? `<p><b>${esc(`${d.cylinder.manufacturer} ${d.cylinder.model}`.trim())}</b>, ${num(d.cylinder.volumeLitres, 0)} litres, recovery ${num(d.cylinder.recoveryMinutes, 0)} minutes${
        d.cylinder.coilAreaM2 !== undefined ? `, coil ≥ ${num(d.cylinder.coilAreaM2, 2)} m²` : ""
      }</p>`
    : `<p>${notDesigned}</p>`,
)}
${item("f", "Design flow temperature", d.designFlowTempC !== null ? `<p><b>${num(d.designFlowTempC, 1)} °C</b> (before any blending)</p>` : `<p>${notDesigned}</p>`)}
${item(
  "g",
  "SCoP at the design flow temperature",
  d.heatPump
    ? `<p><b>${num(d.heatPump.scop, 2)}</b> — ${esc(`${d.heatPump.manufacturer} ${d.heatPump.model}`.trim())}${d.heatPump.scopSource ? ` <span class="small">(source: ${esc(d.heatPump.scopSource)})</span>` : ""}</p>`
    : `<p>${notDesigned}</p>`,
)}
${item("h", "Metering for incentive schemes", d.metering ? `<p>${esc(d.metering)}</p>` : `<p>${notDesigned}</p>`)}`,
  );
}

function propertySection(input: InstallerPackInput): string {
  const p = input.property;
  return section(
    4,
    "Property",
    `${fields([
      ["Built form", valueOr(p.builtForm)],
      ["Property type", valueOr(p.propertyType)],
      ["Main wall construction", valueOr(p.wallConstruction)],
      ["Wall insulation", valueOr(p.wallInsulation)],
      ["Floor construction (under-floor routes possible?)", `${opt(`${tick} Suspended timber`)} ${opt(`${tick} Solid`)} ${opt(`Notes ${blank()}`)}`],
    ])}
<p class="small">Wall type and insulation decide whether pipe can be buried or must run on the surface, and how the outdoor unit's bracket is fixed.</p>`,
  );
}

function plansSection(input: InstallerPackInput): string {
  if (input.plans.length === 0) {
    return section(5, "Floor plans", `<div class="warn">${esc(input.plansNote ?? "No plan is available for this survey.")}</div>`);
  }
  return input.plans
    .map((plan, i) =>
      section(
        5,
        `Floor plan — ${plan.storeyLabel}`,
        `${plan.note ? `<div class="note">${esc(plan.note)}</div>` : ""}
${plan.svg ? `<div class="plan">${plan.svg}</div>` : `<div class="warn">Nothing is drawn on this storey.</div>`}
<p class="small">Pipe lengths are plan-view only: drops to radiators and risers between floors are not drawn — see the allowances in section 9. ${
          i === 0 ? "Mark any change to the route on this page; it becomes the as-built drawing." : ""
        }</p>`,
      ),
    )
    .join("\n");
}

/**
 * The open comments pinned to the plans, numbered as their pins are, beside
 * the plans they are on — "check this radiator position" is read standing at
 * the radiator. Resolved ones are counted only: they are history, and a pack
 * is what to do next. A tick column so the installer can mark each one dealt
 * with on site.
 */
function notesSection(input: InstallerPackInput): string {
  const notes = input.notes;
  if (!notes || (notes.open.length === 0 && notes.resolvedCount === 0)) return "";
  const resolved =
    notes.resolvedCount > 0
      ? `<p class="small">${notes.resolvedCount} resolved ${notes.resolvedCount === 1 ? "note is" : "notes are"} not printed.</p>`
      : "";
  if (notes.open.length === 0) {
    return section(5, "Notes on the plan", `<p>No open notes on the plan.</p>${resolved}`);
  }
  const when = (iso: string) => (/^\d{4}-\d{2}-\d{2}/.test(iso) ? isoDateToLong(iso.slice(0, 10)) : "");
  const rows = notes.open
    .map((n) => {
      const replies = n.replies
        .map((r) => `<div class="reply">${r.author ? `<b>${esc(r.author)}:</b> ` : ""}${esc(r.body)}</div>`)
        .join("");
      const by = [n.author, when(n.createdAt)].filter((s): s is string => !!s).join(", ");
      return `<tr><td class="c"><span class="pin-no">${n.number}</span></td><td>${esc(n.storeyLabel)}</td><td>${valueOr(
        n.about,
        "—",
      )}</td><td><div class="note-body">${esc(n.body)}</div>${replies}${
        by ? `<div class="small">${esc(by)}</div>` : ""
      }</td><td class="c">${tick}</td></tr>`;
    })
    .join("");
  return section(
    5,
    "Notes on the plan",
    `<table class="notes"><thead><tr><th class="c">Pin</th><th>Storey</th><th>On</th><th>Note</th><th class="c">Done</th></tr></thead><tbody>
${rows}
</tbody></table>
<p class="small">Each note is pinned on the floor plan above at its number. Raise anything you cannot resolve on site with the designer before you work round it.</p>${resolved}`,
  );
}

function outdoorUnitSection(input: InstallerPackInput): string {
  const units = input.outdoorUnits;
  const body =
    units.length === 0
      ? `<div class="warn">No outdoor unit is drawn on the plan.${input.heatPumpName ? ` The design names ${esc(input.heatPumpName)}; confirm its position with the designer.` : ""}</div>`
      : units
          .map(
            (u) => `<h3>${esc(u.tag)} — ${esc(u.name)}</h3>
${fields([
  ["Casing W × D × H", `${mmText(u.widthMm)} × ${mmText(u.depthMm)} × ${mmText(u.heightMm)} mm${u.fromCatalogue ? "" : ` <span class="small">(entered by hand, not from the catalogue)</span>`}`],
  ["Position", `As drawn on the ${esc(u.storeyLabel)} plan (${esc(u.tag)})`],
  ["Clearances as installed (mm)", ["Back", "Front", "Left", "Right", "Above"].map((side) => opt(`${side} ${blank("s")}`)).join(" ")],
  ["Manufacturer's minimum clearances checked", `${tick}`],
  ["Base / bracket", `${opt(`${tick} Concrete base`)} ${opt(`${tick} Ground frame`)} ${opt(`${tick} Wall bracket`)} ${opt(`Other ${blank()}`)}`],
  ["Anti-vibration feet fitted", `${tick}`],
  ["Condensate route (MIS 3005-I 3.6.6)", blank("l")],
  ["Anti-freeze valve position", blank("l")],
])}`,
          )
          .join("");
  const plant = input.plant ?? [];
  const plantTable =
    plant.length === 0
      ? ""
      : `<h3>Cylinder and buffer drawn on the plan</h3>
<table><thead><tr><th>Tag</th><th>What</th><th>Storey</th><th>Room</th><th class="n">Litres</th><th class="n">Ø × H (mm)</th></tr></thead><tbody>
${plant
  .map(
    (v) =>
      `<tr><td><b>${esc(v.tag)}</b></td><td>${esc(v.name)}${v.nominal ? ` <span class="small">(nominal — confirm with the chosen product)</span>` : ""}</td><td>${esc(v.storeyLabel)}</td><td>${valueOr(v.room, "—")}</td><td class="n">${num(v.volumeLitres, v.volumeLitres % 1 === 0 ? 0 : 1)}</td><td class="n">${mmText(v.diameterMm)} × ${mmText(v.heightMm)}</td></tr>`,
  )
  .join("")}
</tbody></table>
<p class="small">Check the space: the footprint drawn is the vessel's diameter only — allow for pipework, the expansion vessel and access to the controls.</p>`;
  return section(6, plant.length > 0 ? "Outdoor unit and indoor plant" : "Outdoor unit", body + plantTable);
}

function radiatorSection(input: InstallerPackInput): string {
  const rows = input.radiators;
  const body =
    rows.length === 0
      ? `<div class="warn">No radiators are drawn on the plan.</div>`
      : `<table><thead><tr><th>Tag</th><th>Storey</th><th>Room</th><th>Type</th><th class="n">H × L (mm)</th><th class="n">Depth (mm)</th><th>Plan note</th><th>Keep / replace</th><th>Valves</th></tr></thead><tbody>
${rows
  .map(
    (r) =>
      `<tr><td><b>${esc(r.tag)}</b></td><td>${esc(r.storeyLabel)}</td><td>${valueOr(r.room, "—")}</td><td>${esc(r.panelType)} <span class="small">(${esc(r.typeAlias)})</span></td><td class="n">${mmText(r.heightMm)} × ${mmText(r.lengthMm)}</td><td class="n">${mmText(r.depthMm)}</td><td>${r.label ? esc(r.label) : ""}</td><td>${opt(`${tick} Keep`)} ${opt(`${tick} Replace`)}</td><td>${opt(`${tick} TRV + LS`)} ${opt(`${tick} LS only`)}</td></tr>`,
  )
  .join("")}
</tbody></table>
<p class="small">Depth is Purmo Compact's nominal figure for the panel type. Rooms come from the scan's floor outlines; "—" means the radiator is not inside a captured floor.</p>`;
  return section(7, "Radiator schedule", body);
}

function pipeSection(input: InstallerPackInput): string {
  const runs = input.pipeRuns;
  const totals: readonly PipeDiameterTotal[] = input.stock.pipeTotals;
  const runTable =
    runs.length === 0
      ? `<div class="warn">No pipe runs are drawn on the plan.</div>`
      : `<table><thead><tr><th>Tag</th><th>Storey</th><th>Circuit</th><th class="n">Ø (mm)</th><th>Material</th><th class="n">Plan length (m)</th><th class="n">Bends</th><th>Plan note</th></tr></thead><tbody>
${runs
  .map(
    (r) =>
      `<tr><td><b>${esc(r.tag)}</b></td><td>${esc(r.storeyLabel)}</td><td class="${r.circuit === "flow" ? "flow" : "ret"}">${r.circuit === "flow" ? "Flow" : "Return"}</td><td class="n">${r.diameterMm}</td><td>${esc(
        PIPE_MATERIAL_NAMES[pipeMaterialOf(r)],
      )}</td><td class="n">${num(r.lengthM, 2)}</td><td class="n">${r.bends}</td><td>${r.label ? esc(r.label) : ""}</td></tr>`,
  )
  .join("")}
</tbody></table>`;
  const bought = (t: PipeDiameterTotal) =>
    t.stock.pieces.length === 0
      ? "—"
      : t.stock.pieces
          .map((p) => `${p.count} × ${num(p.lengthM, p.lengthM % 1 === 0 ? 0 : 1)} m ${t.stock.unit === "coil" ? "coil" : "length"}${p.count === 1 ? "" : "s"}`)
          .join(" + ");
  const totalTable =
    totals.length === 0
      ? ""
      : `<h3>Totals per size and material</h3><table><thead><tr><th class="n">Ø (mm)</th><th>Material</th><th class="n">Flow (m)</th><th class="n">Return (m)</th><th class="n">Drops (m)</th><th class="n">Extra (m)</th><th class="n">Total (m)</th><th class="n">+${num(input.stock.settings.wastagePct, 0)}% (m)</th><th>Bought as</th></tr></thead><tbody>
${totals
  .map(
    (t) =>
      `<tr><td class="n"><b>${t.diameterMm}</b></td><td>${esc(PIPE_MATERIAL_NAMES[t.material])}</td><td class="n">${num(t.flowM, 2)}</td><td class="n">${num(t.returnM, 2)}</td><td class="n">${num(t.dropsM, 2)}</td><td class="n">${num(t.extraM, 2)}</td><td class="n">${num(t.totalM, 2)}</td><td class="n">${num(t.orderM, 2)}</td><td><b>${esc(bought(t))}</b></td></tr>`,
  )
  .join("")}
</tbody></table>`;
  return section(
    8,
    "Pipe schedule",
    `${runTable}${totalTable}
<p class="small">Material as drawn on the plan; a run drawn before materials were recorded is copper. ${opt(`Riser positions ${blank()}`)}</p>`,
  );
}

function pickRow(line: StockLine): string {
  const qtyCell = line.overridden
    ? `${qtyText(line.quantity)}<span class="derived">derived ${line.derivedQuantity === null ? "—" : qtyText(line.derivedQuantity)}</span>`
    : qtyText(line.quantity);
  const extra = [line.measure, line.note ? `Note: ${line.note}` : null].filter(Boolean).join(" · ");
  return `<tr><td class="c">${tick}</td><td><b>${esc(line.item)}</b>${line.spec ? `<br>${esc(line.spec)}` : ""}${
    extra ? `<div class="basis"><b>${esc(extra)}</b></div>` : ""
  }<div class="basis"><span class="src">${esc(SOURCE_TEXT[line.source] ?? line.source)}</span> ${esc(line.basis)}</div></td><td class="n qty">${qtyCell}</td><td>${esc(
    line.unit,
  )}</td><td class="write"></td><td class="write"></td></tr>`;
}

function stockSection(input: InstallerPackInput): string {
  const report = input.stock;
  const excluded = report.lines.filter((l) => l.excluded).length;
  const groups = report.groups
    .map((g) => {
      const lines = g.lines.filter((l) => !l.excluded);
      if (lines.length === 0 && g.notes.length === 0) return "";
      return `<h3>${esc(g.title)}</h3>
${
  lines.length > 0
    ? `<table class="pick"><thead><tr><th class="c" style="width:7mm">✓</th><th>Item</th><th class="n" style="width:16mm">Qty</th><th style="width:13mm">Unit</th><th style="width:18mm">Qty taken</th><th style="width:20mm">Qty returned</th></tr></thead><tbody>
${lines.map(pickRow).join("\n")}
</tbody></table>`
    : ""
}
${g.notes.map((n) => `<div class="note">${esc(n)}</div>`).join("")}`;
    })
    .join("\n");

  const s = report.settings;
  const material = (m: PlanPipeMaterial | undefined) => PIPE_MATERIAL_NAMES[m ?? "copper"];
  const settingRows: [string, string, keyof typeof STOCK_SETTING_BASIS][] = [
    ["Drop allowance per radiator", `${num(s.dropPerRadiatorM, 2)} m at ${s.dropDiameterMm} mm`, "dropPerRadiatorM"],
    ["Drops made of", material(s.dropMaterial), "dropMaterial"],
    [
      "Extra pipe (risers, wall runs)",
      s.extraPipe.length === 0
        ? "None"
        : s.extraPipe.map((e) => `${num(e.metres, 2)} m of ${e.diameterMm} mm ${material(e.material)}`).join(", "),
      "extraPipe",
    ],
    ["Wastage", `${num(s.wastagePct, 0)}%`, "wastagePct"],
    ["Pipe stock length", `${num(s.stockLengthM, 1)} m`, "stockLengthM"],
    ["Plastic pipe coil", s.coilLengthM > 0 ? `${num(s.coilLengthM, 0)} m` : "Listed coil sizes", "coilLengthM"],
    ["Insulation coverage", `${num(s.insulationCoveragePct, 0)}%`, "insulationCoveragePct"],
    ["Glycol", s.glycolConcentrationPct > 0 ? `${num(s.glycolConcentrationPct, 0)}%` : "None", "glycolConcentrationPct"],
  ];

  return section(
    9,
    "Stock and pick list",
    `${report.warnings.map((w) => `<div class="warn">${esc(w)}</div>`).join("")}
<p class="small">Grouped in loading order. Tick each line as it goes on the van; write what was taken and what came back. Each quantity's basis is in small print —
labels: <b>plan</b> counted off the drawing, <b>design</b> from the design, <b>R</b> read in a standard, <b>V</b> a vendor's figure, <b>I</b> inferred from the drawing,
<b>RoT</b> rule of thumb, <b>assumed</b> a default to check, <b>catalogue</b> your stock list's rule, <b>manual</b> added for this job.${
      excluded > 0 ? ` ${excluded} line${excluded === 1 ? " was" : "s were"} excluded in Scannix and ${excluded === 1 ? "is" : "are"} not listed.` : ""
    }</p>
${groups}
<h3>Allowances used</h3>
<table><thead><tr><th>Setting</th><th>Value</th><th>Why</th></tr></thead><tbody>
${settingRows
  .map(([label, value, key]) => `<tr><td>${esc(label)}</td><td class="n">${esc(value)}</td><td class="small">${esc(STOCK_SETTING_BASIS[key].text)}</td></tr>`)
  .join("")}
</tbody></table>`,
  );
}

function commissioningSection(input: InstallerPackInput): string {
  const d = input.design;
  const row = (check: string, design: string, measured: string) =>
    `<tr><td>${esc(check)}</td><td>${design}</td><td>${measured}</td></tr>`;
  // A treatment product as the pick list carries it: its name and how much, with the unit.
  const productFor = (pattern: RegExp) => {
    const found = input.stock.lines.find((l) => !l.excluded && pattern.test(l.item));
    return found ? `${esc(found.item)} — ${qtyText(found.quantity)} ${esc(found.unit)}` : null;
  };
  const volume = input.stock.systemVolume;
  const volumeTable =
    volume.lines.length === 0
      ? `<div class="warn">Nothing on the plan holds water that Scannix can count — the system volume is not known.</div>`
      : `<h3>System water volume</h3>
<table><thead><tr><th style="width:34%">Part</th><th class="n" style="width:12%">Litres</th><th>Basis</th></tr></thead><tbody>
${volume.lines
  .map(
    (l) =>
      `<tr><td>${esc(l.component)}</td><td class="n">${l.litres === null ? `<span class="nd">not included</span>` : num(l.litres, 1)}</td><td class="small"><span class="src">${esc(
        SOURCE_TEXT[l.source] ?? l.source,
      )}</span> ${esc(l.basis)}</td></tr>`,
  )
  .join("")}
</tbody><tfoot><tr><td><b>${volume.notIncluded.length > 0 ? "Counted so far (a minimum)" : "Total"}</b></td><td class="n"><b>${num(volume.knownLitres, 1)}</b></td><td class="small">${
          volume.notIncluded.length > 0
            ? `Not included: ${esc(volume.notIncluded.join("; "))}. Doses below are worked from the counted litres — add the rest before dosing.`
            : "Every part on the job is counted."
        }</td></tr></tfoot></table>`;
  const glycol = input.stock.glycol;
  const antiFreeze = glycol
    ? `Glycol ${num(glycol.concentrationPct, 0)}% — ${glycol.litres} L`
    : productFor(/inhibitor/i) ?? `<span class="muted">Not listed</span>`;
  return section(
    10,
    "Water treatment and commissioning",
    `${volumeTable}
<p class="small">Fields follow MIS 3005-I:2025 Appendix A (example commissioning checklist) and BS 7593. The design value is printed where Scannix holds one; the measured column is for site. Also follow the manufacturer's own checklist — the warranty may depend on it.</p>
<table><thead><tr><th style="width:38%">Check</th><th style="width:30%">Design / expected</th><th>Measured / done</th></tr></thead><tbody>
${row("Heating system flushed and cleaned (BS 7593; AD L 8.8)", "Required", `${tick} &nbsp;Date ${blank("s")}`)}
${row("Cleaner used", productFor(/cleaner/i) ?? `<span class="muted">Not listed</span>`, `Product ${blank()}`)}
${row("Inhibitor / anti-freeze used", antiFreeze, `${opt(`Product ${blank("s")}`)} ${opt(`Conc. ${blank("s")}`)}`)}
${glycol ? row("Inhibitor used with the glycol", productFor(/inhibitor/i) ?? `<span class="muted">Per the glycol maker</span>`, `Product ${blank()}`) : ""}
${row("Strainers / filters clear", "Required", tick)}
${row("Expansion vessel pre-charge", `<span class="muted">Not designed in Scannix — per manufacturer / system volume</span>`, `${blank("s")} bar`)}
${row("System cold-fill pressure", `<span class="muted">Per manufacturer</span>`, `${blank("s")} bar`)}
${row("Flow temperature at commissioning", d.designFlowTempC !== null ? `${num(d.designFlowTempC, 1)} °C design` : notDesigned, `${blank("s")} °C`)}
${row("Heat pump model and serial number", input.heatPumpName ? esc(input.heatPumpName) : notDesigned, `S/N ${blank()}`)}
${row("Cylinder model and serial number", d.cylinder ? esc(`${d.cylinder.manufacturer} ${d.cylinder.model}`.trim()) : notDesigned, `S/N ${blank()}`)}
${row("Outdoor unit clearances (mm)", "Per manufacturer's manual", ["B", "F", "L", "R"].map((side) => opt(`${side} ${blank("s")}`)).join(" "))}
${row("Pipework insulated and vapour sealed", "All primaries (see section 9)", tick)}
${row("G3 commissioning certificate completed", "Required where an unvented cylinder is fitted", tick)}
${row("System installed as per design", "Yes — otherwise record in section 11", tick)}
${row(
  "Radiators balanced",
  d.designFlowTempC === null
    ? notDesigned
    : d.systemDeltaT && !d.systemDeltaT.assumed
      ? `Flow − return ${num(d.systemDeltaT.kelvin, 1)} K <span class="small">(the designer's figure, which the design is calculated at)</span>`
      : `Flow − return ${num(d.systemDeltaT?.kelvin ?? 5, 0)} K <span class="small">(assumed — not set in the design decisions; the design pack's figure)</span>`,
  tick,
)}
${row("Customer given all documentation required by MIS 3005-D and -I", "Required", tick)}
</tbody></table>`,
  );
}

/**
 * What the surveyor found, section by section, at the BACK of the pack.
 *
 * At the back rather than beside the job card on purpose: this is reference —
 * the fitter reads it when a question comes up ("which way does the D2 run?",
 * "is there a spare way on the board?"), not while unloading the van. Section
 * 2 points at it so it is known to be there.
 *
 * Only ANSWERED questions are printed. A page of "Not recorded" tells an
 * installer nothing and buries the handful of answers that matter; a question
 * missing from this list means nobody answered it, which the portal is where
 * to fix.
 */
function siteSurveySection(input: InstallerPackInput): string {
  const sections = (input.siteSurvey ?? []).filter((s) => s.answers.length > 0);
  if (sections.length === 0) return "";
  const body = sections
    .map(
      (s) => `<h3>${esc(s.title)}</h3>
${fields(s.answers.map((a) => [a.label, valueOr(a.value)] as const))}`,
    )
    .join("\n");
  return section(
    13,
    "Site survey \u{2014} what the surveyor found",
    `<p class="small">Recorded on the survey visit. Photographs of each of these are on the survey in the portal.</p>
${body}`,
    { pageBreak: true },
  );
}

function deviationsSection(): string {
  return section(
    11,
    "Deviations, as-built notes and sign-off",
    `<p class="small">Record every change from this pack — route, radiator, pipe size, position of the outdoor unit — with the reason and a photo reference. Mark route changes on the plans.</p>
<table><thead><tr><th style="width:36%">What changed</th><th style="width:36%">Why</th><th>Photo ref</th></tr></thead><tbody>
${Array.from({ length: 8 }, () => `<tr><td class="write"></td><td class="write"></td><td class="write"></td></tr>`).join("")}
</tbody></table>
<h3>Installer sign-off</h3>
${fields([
  ["Installed by (name)", blank("l")],
  ["Signature", blank("l")],
  ["Date completed", blank()],
  ["Checked by (technical supervisor)", blank("l")],
])}`,
  );
}

function returnsSection(): string {
  const items = [
    "Commissioning record (MIS 3005-I Appendix A) completed and signed",
    "Heat pump, cylinder and controls serial numbers",
    "G3 (unvented) commissioning certificate",
    "Electrical installation certificate (EIC)",
    `Building Regulations notification number ${blank()}`,
    `DNO reference ${blank()}`,
    "As-built drawings — the plans in this pack, marked up",
    "Manufacturer's manuals and warranty cards",
    "Photos: insulation continuity, outdoor unit, plant room, labelled valves",
    "Unused stock returned and counted (section 9)",
  ];
  return section(
    12,
    "Returns for the customer handover pack",
    `<p class="small">Bring these back to the office. They feed the customer's handover pack.</p>
<table><tbody>${items.map((i) => `<tr><td class="c" style="width:8mm">${tick}</td><td>${i}</td></tr>`).join("")}</tbody></table>`,
  );
}

// ------------------------------------------------------------ template --

export const installerPackTemplate: DocumentTemplate<InstallerPackInput> = {
  type: "installer-pack",
  // 1.1.0 (2026-09-11): numbered comment pins on the plans and 'Notes on the plan'.
  // 1.2.0 (2026-09-11): pipe material, cylinder and buffer, system water volume and treatment doses, the designer's ΔT.
  // 1.3.0 (2026-09-11): plan labels laid out clear of each other and of the pins (leaders where moved); storeys named "First floor", not "Level 1".
  version: "installer-pack@1.3.0",
  standardsRef:
    "Installer job pack. Design brief per MIS 3005-D:2025 cl. 3.8.1 (a)–(h); commissioning fields per MIS 3005-I:2025 Appendix A; " +
    "stock rules per docs/research/installer-pack-2026-09-10.md",

  render(input: InstallerPackInput): RenderedDocument {
    const { header } = input;
    const title = `Installer pack${input.issued ? "" : " (DRAFT)"}`;
    const body = [
      coverSection(input),
      preStartSection(),
      designSection(input),
      propertySection(input),
      plansSection(input),
      notesSection(input),
      outdoorUnitSection(input),
      radiatorSection(input),
      pipeSection(input),
      stockSection(input),
      commissioningSection(input),
      deviationsSection(),
      returnsSection(),
      siteSurveySection(input),
    ].join("\n");

    const html = `<!DOCTYPE html>
<html lang="en-GB">
<head>
<meta charset="utf-8">
<meta name="sts:pack-date" content="${esc(header.issuedOn)}">
<title>${esc(`${title} — ${header.jobReference}`)}</title>
<style>${STYLES}</style>
</head>
<body>
${input.issued ? "" : `<div class="draft-mark" aria-hidden="true">DRAFT</div>`}
<header class="doc-header">
<div>
<h1>${esc(title)}</h1>
<div>${esc(header.propertyAddress)}</div>
</div>
<div class="meta">
<div><b>${esc(header.organisationName)}</b></div>
<div>Job <b>${esc(header.jobReference)}</b></div>
<div>${input.issued ? "Issued" : "Printed"} ${esc(isoDateToLong(header.issuedOn))}</div>
${header.preparedBy ? `<div>Prepared by ${esc(header.preparedBy)}</div>` : ""}
${header.mcsNumber ? `<div>MCS ${esc(header.mcsNumber)}</div>` : ""}
</div>
</header>
${input.issued ? "" : `<div class="draft-banner">DRAFT — not issued. Quantities and the design can still change; install from an issued pack.</div>`}
${body}
<footer>${esc(installerPackFooterText(header, input.issued))} · template ${esc(installerPackTemplate.version)} · ${esc(installerPackTemplate.standardsRef)}</footer>
</body>
</html>`;

    return {
      title: `${title} — ${header.propertyAddress}`,
      templateVersion: installerPackTemplate.version,
      html,
    };
  },
};
